import type { Page } from '@/components/canvas/document/publicTypes';
import type { Block, BlockPatch, BlockType } from '@/components/canvas/blocks/types';
import {
  getBlockDefinition,
  hasBlockDefinition,
} from '@/components/canvas/blocks/registry/blockRegistry';
import {
  layoutCenteredOnAnchor,
  resolveElementInsertContext,
  resolveTextInsertContext,
} from '@/components/canvas/workspace/model/elementInsertTarget';
import type { HistorySnapshot } from '../types';
import type { SliceDeps } from '../deps';

function ensurePageBlocks(page: Page): Block[] {
  if (!page.blocks) page.blocks = [];
  return page.blocks;
}

function nextBlockZIndex(page: Page): number {
  let maxZ = 0;
  for (const el of page.elements) maxZ = Math.max(maxZ, el.layout.zIndex);
  for (const b of page.blocks ?? []) maxZ = Math.max(maxZ, b.layout.zIndex);
  return maxZ + 1;
}

function findBlockOnPage(page: Page, blockId: string) {
  const block = (page.blocks ?? []).find((b) => b.id === blockId);
  return block ? { page, block } : null;
}

function findBlockInSnapshot(draft: HistorySnapshot, blockId: string) {
  for (const page of draft.pages) {
    const hit = findBlockOnPage(page, blockId);
    if (hit) return hit;
  }
  return null;
}

function insertBlockOnPage(page: Page, block: Block) {
  return {
    selectedBlockIds: [block.id],
    selectedElementIds: [] as string[],
    isPageSelected: false,
    selectedPageIds: [] as string[],
    selectionScope: null as null,
    block,
    pageId: page.id,
  };
}

export function createBlocksSlice({ set, get, mutate, mutateSilent }: SliceDeps) {
  return {
    updateBlock: (id: string, patch: BlockPatch) => {
      mutate((draft) => {
        const hit = findBlockInSnapshot(draft, id);
        if (!hit) return;
        const { page, block } = hit;
        if (patch.layout) {
          block.layout = { ...block.layout, ...patch.layout };
        }
        if (patch.data) {
          block.data = { ...block.data, ...patch.data } as typeof block.data;
        }
        if (patch.name !== undefined) block.name = patch.name;
        page.updatedAt = Date.now();
      });
    },

    updateBlockSilent: (id: string, patch: BlockPatch) => {
      mutateSilent((draft) => {
        const block = (draft.blocks ?? []).find((b) => b.id === id);
        if (!block) return;
        if (patch.layout) {
          block.layout = { ...block.layout, ...patch.layout };
        }
        if (patch.data) {
          block.data = { ...block.data, ...patch.data } as typeof block.data;
        }
        draft.updatedAt = Date.now();
      });
    },

    updateBlockData: (id: string, patch: Partial<Block['data']>) => {
      mutate((draft) => {
        const hit = findBlockInSnapshot(draft, id);
        if (!hit) return;
        hit.block.data = { ...hit.block.data, ...patch } as typeof hit.block.data;
        hit.page.updatedAt = Date.now();
      });
    },

    setBlockPositions: (updates: { id: string; x: number; y: number }[]) => {
      mutate((draft) => {
        for (const u of updates) {
          const hit = findBlockInSnapshot(draft, u.id);
          if (!hit) continue;
          hit.block.layout.x = u.x;
          hit.block.layout.y = u.y;
          hit.page.updatedAt = Date.now();
        }
      });
    },

    commitBlockLayout: (
      id: string,
      layout: { x: number; y: number; width: number; height: number },
    ) => {
      mutate((draft) => {
        const hit = findBlockInSnapshot(draft, id);
        if (!hit) return;
        hit.block.layout = { ...hit.block.layout, ...layout };
        hit.page.updatedAt = Date.now();
      });
    },

    addBlock: (type: BlockType) => {
      if (!hasBlockDefinition(type)) return;
      const def = getBlockDefinition(type)!;
      const s = get();
      const ctx = resolveElementInsertContext(
        s.pages,
        s.currentPageId,
        s.panX,
        s.panY,
        s.zoom,
      );
      const page = ctx.page;
      if (!page) return;
      const { width, height } = def.defaultSize;
      const { x, y } = layoutCenteredOnAnchor(ctx.anchorX, ctx.anchorY, width, height);
      const block = def.create({
        x,
        y,
        width,
        height,
        zIndex: nextBlockZIndex(page),
      });
      mutate((draft) => {
        const p = draft.pages.find((pv) => pv.id === page.id);
        if (!p) return;
        ensurePageBlocks(p).push(block);
        p.updatedAt = Date.now();
      });
      const sel = insertBlockOnPage(page, block);
      set({
        selectedBlockIds: sel.selectedBlockIds,
        selectedElementIds: sel.selectedElementIds,
        isPageSelected: sel.isPageSelected,
        selectedPageIds: sel.selectedPageIds,
        selectionScope: sel.selectionScope,
      });
    },

    addBlockAt: (type: BlockType, payload: { x: number; y: number; pageId?: string }) => {
      if (!hasBlockDefinition(type)) return;
      const def = getBlockDefinition(type)!;
      const s = get();
      const ctx = resolveTextInsertContext(
        s.pages,
        s.currentPageId,
        { x: payload.x, y: payload.y },
        'page',
        payload.pageId,
      );
      const page = ctx.page;
      if (!page) return;
      const block = def.create({
        x: payload.x,
        y: payload.y,
        width: def.defaultSize.width,
        height: def.defaultSize.height,
        zIndex: nextBlockZIndex(page),
      });
      mutate((draft) => {
        const p = draft.pages.find((pv) => pv.id === page.id);
        if (!p) return;
        ensurePageBlocks(p).push(block);
        p.updatedAt = Date.now();
      });
      const sel = insertBlockOnPage(page, block);
      set({
        selectedBlockIds: sel.selectedBlockIds,
        selectedElementIds: sel.selectedElementIds,
        isPageSelected: sel.isPageSelected,
        selectedPageIds: sel.selectedPageIds,
        selectionScope: sel.selectionScope,
      });
    },

    /** @deprecated 使用 addBlock('cutdown-card') */
    addCutdownCardBlock: () => {
      get().addBlock('cutdown-card');
    },

    /** @deprecated 使用 addBlockAt('cutdown-card', payload) */
    addCutdownCardBlockAt: (payload: { x: number; y: number; pageId?: string }) => {
      get().addBlockAt('cutdown-card', payload);
    },

    deleteSelectedBlocks: () => {
      const { selectedBlockIds } = get();
      if (selectedBlockIds.length === 0) return;
      const toDelete = new Set(selectedBlockIds);
      mutate((draft) => {
        for (const page of draft.pages) {
          if (!page.blocks?.length) continue;
          const next = page.blocks.filter((b) => !toDelete.has(b.id));
          if (next.length !== page.blocks.length) {
            page.blocks = next;
            page.updatedAt = Date.now();
          }
        }
      });
      set({ selectedBlockIds: [] });
    },
  };
}

export type BlocksSliceActions = ReturnType<typeof createBlocksSlice>;
